/**
 * Service for sentry (Raven.js)
 * Kept the naming the same as the segement service incase we combine the two later.
 *
 * @author Matthew Rawson @ 17/10/2017 17:48
 */
import Raven from "raven-js"
import {AxiosError} from "axios"
import AuthStream from "@irati/auth/streams/auth"
import UnauthedStream from "@irati/auth/streams/unauthed"
import GlobalStream from "@irati/auth/streams/global"
import ObserveWithPriority from "@irati/auth/helpers/auth/ObserveWithPriority"
import {STAGING, PRODUCTION} from "../../constants/environment"
import parseEmail from "./helpers/parseEmail"
import shouldSendCallback from "./helpers/shouldSendCallback"
import dataCallback from "./helpers/dataCallback"
import getSentryUrl from "./helpers/getSentryDNS"
import env from "../../env"

// only allow our domain
const whitelistUrls = [/perkbox\./]

const ignoreErrors = [
  /Network Error/, // device error not our app
  /status code 401/, // refresh tokens use this
  /timeout of 0ms exceeded/ // annoying
]

if ([STAGING, PRODUCTION].indexOf(env.ENVIRONMENT_NAME) > -1) {
  const key = getSentryUrl(env.ENVIRONMENT_NAME)
  Raven.config(`https://${key}`, {
    release: `${env.ENVIRONMENT_NAME}__${env.BUNDLE_REF}`,
    environment: env.ENVIRONMENT_NAME,
    whitelistUrls,
    ignoreErrors,
    shouldSendCallback,
    dataCallback
  }).install()
}

setTimeout(() => {
  new ObserveWithPriority({streams: [AuthStream, UnauthedStream, GlobalStream], key: "user"}).subscribe((data) => {
    if (haveUser(data)) {
      Raven.setUserContext({
        id: data.user.uuid,
        email: parseEmail(data.user.email),
        username: data.user.firstname
      })
    } else {
      Raven.setUserContext()
    }
  })
})

/**
 * Needs to wrap the app init to ensure all context is captured. Doesn't matter if it isn't in a pipeline environment
 * or if the Raven.config().install() hasn't run.
 *
 * @param: function - contains the react.render function
 */
const context = (func: () => void): void => {
  Raven.context(() => {
    /** Inject sitewide context */
    Raven.setExtraContext({
      // none?
    })
    func()
  })
}

/**
 * Sending the whole error stack + msg to sentry. Also sends error.response if exists
 *
 * @author Richard Scoones @ 27/10/2017
 */
const captureException = (e: AxiosError) => {
  Raven.context(() => {
    const error: any = {
      name: e.name,
      message: e.message,
      stack: e.stack
    }
    if (e.response) {
      error.response = e.response.data
      error.config = e.response.config
    }
    Raven.captureException(e, {extra: {error}})
  })
}

export default {
  context,
  captureException
}

function haveUser(data: any) {
  if (data && data.user) {
    return data.user.uuid || data.user.email
  }

  return false
}
